import type { ReactElement } from 'react';
import { mealTypeIcon } from '../../api/types.js';
import type { Recipe } from '../../api/types.js';

interface RecipeDetailProps {
  recipe: Recipe;
  onEdit: () => void;
  onDelete: () => void;
}

export function RecipeDetail({
  recipe,
  onEdit,
  onDelete,
}: RecipeDetailProps): ReactElement {
  const steps = recipe.instructions
    .split('\n')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);

  function onDeleteClick(): void {
    const ok = window.confirm(`Delete "${recipe.title}"? This cannot be undone.`);
    if (ok) onDelete();
  }

  return (
    <section className="flex flex-col gap-4 p-4" data-testid="recipe-detail">
      <header className="flex flex-col gap-1">
        <div className="flex items-center gap-2 text-2xl font-semibold">
          <span aria-hidden="true">{mealTypeIcon(recipe.meal_type)}</span>
          <span>{recipe.title}</span>
        </div>
        <div className="text-sm" style={{ color: 'var(--text-muted)' }}>
          🕒 {recipe.prep_time_minutes} Min · 👥 {recipe.portions} Port. ·{' '}
          {recipe.per_portion.kcal} kcal/portion
        </div>
        <div className="text-sm" style={{ color: 'var(--text-muted)' }}>
          P {recipe.per_portion.protein_g}g · C {recipe.per_portion.carbs_g}g · F{' '}
          {recipe.per_portion.fat_g}g
        </div>
        {recipe.source_url ? (
          <a
            href={recipe.source_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs underline"
            style={{ color: 'var(--accent)' }}
          >
            Source
          </a>
        ) : null}
      </header>

      <div className="flex gap-2">
        <button
          type="button"
          onClick={onEdit}
          className="rounded-md border px-3 py-2 text-sm"
          style={{
            borderColor: 'var(--border)',
            color: 'var(--text-main)',
            minHeight: 40,
          }}
          data-testid="recipe-edit"
        >
          ✏️ Edit
        </button>
        <button
          type="button"
          onClick={onDeleteClick}
          className="rounded-md border px-3 py-2 text-sm"
          style={{
            borderColor: 'var(--border)',
            color: '#dc2626',
            minHeight: 40,
          }}
          data-testid="recipe-delete"
        >
          🗑️ Delete
        </button>
      </div>

      <div className="rounded-xl border p-3" style={{ borderColor: 'var(--border)' }}>
        <h3 className="mb-2 text-sm font-semibold">Instructions</h3>
        {steps.length === 0 ? (
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
            No instructions provided.
          </p>
        ) : (
          <ol className="list-decimal pl-5 text-sm">
            {steps.map((s, i) => (
              <li key={i} className="mb-1">
                {s}
              </li>
            ))}
          </ol>
        )}
      </div>

      <div className="rounded-xl border p-3" style={{ borderColor: 'var(--border)' }}>
        <h3 className="mb-2 text-sm font-semibold">Ingredients</h3>
        <table className="w-full text-sm">
          <thead>
            <tr style={{ color: 'var(--text-muted)' }}>
              <th className="text-left font-normal">Name</th>
              <th className="text-right font-normal">Amount</th>
              <th className="text-left font-normal">Unit</th>
              <th className="text-right font-normal">kcal</th>
            </tr>
          </thead>
          <tbody>
            {recipe.ingredients.map((line) => (
              <tr key={line.ingredient_id} className="border-t" style={{ borderColor: 'var(--border)' }}>
                <td className="py-1">{line.name}</td>
                <td className="py-1 text-right">{line.amount}</td>
                <td className="py-1">{line.unit}</td>
                <td className="py-1 text-right">{line.kcal}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}